import { Link } from 'react-router-dom';
import { ArrowRight, UserPlus, Package, Wrench, Cpu, Send, Coins } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { FadeUp, LineReveal } from '../components/Reveal';
import BrowserFrame from '../components/BrowserFrame';
import { StudioHomeMock, BuilderCanvasMock, ConfigPanelMock, TestConsoleMock } from '../components/StudioMock';

const steps = [
  { icon: UserPlus, en: ['Sign up', 'Create your business account in minutes — no card required to explore.'], mock: null },
  { icon: Package, en: ['Get free predefined agents instantly', 'Pre-built agents are live in your Studio from day one. No cold start.'], mock: <StudioHomeMock /> },
  { icon: Wrench, en: ['Customize & build with the Studio', 'Assemble staged workflows from your org\'s real Apex and Flow actions — no code.'], mock: <BuilderCanvasMock /> },
  { icon: Cpu, en: ['Choose your LLM', 'Claude, GPT, or Gemini — pick per agent, switch any time, no lock-in.'], mock: <ConfigPanelMock /> },
  { icon: Send, en: ['Deploy to any channel', 'WhatsApp, Instagram, Teams, Slack, or a web widget.'], mock: <TestConsoleMock /> },
  { icon: Coins, en: ['Buy credits as you scale', 'Start with $49 minimum. Credits are only consumed when agents actually run.'], mock: null },
];

export default function HowItWorks() {
  const { t } = useLang();
  return (
    <div>
      <section className="bg-[#10282C] grain relative overflow-hidden pt-36 pb-24">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00C5C2]">{t('how.kicker')}</p>
          <h1 className="mt-5 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-3xl">
            <LineReveal lines={[t('how.title')]} />
          </h1>
        </div>
      </section>

      <section className="py-24 bg-[#F6F8F7]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="relative">
            <div className="absolute left-[21px] top-4 bottom-4 w-px bg-[#10282C]/12 hidden sm:block" />
            <div className="space-y-16">
              {steps.map(({ icon: Icon, en, mock }, i) => (
                <FadeUp key={i} className="relative sm:pl-20">
                  <span className="hidden sm:flex absolute left-0 top-0 w-11 h-11 rounded-full bg-[#10282C] text-[#00C5C2] items-center justify-center border-4 border-[#F6F8F7]">
                    <Icon size={17} strokeWidth={1.9} />
                  </span>
                  <p className="text-[11px] font-mono tracking-[0.16em] text-[#00918F]">STEP {i + 1} / 6</p>
                  <h2 className="mt-2 font-display text-xl sm:text-2xl font-semibold tracking-tight text-[#10282C]">{en[0]}</h2>
                  <p className="mt-2.5 text-base text-[#10282C]/60 leading-relaxed max-w-xl">{en[1]}</p>
                  {mock && (
                    <div className="mt-7">
                      <BrowserFrame url="yourorg.lightning.force.com/gyeno/studio" className={i === 4 ? 'max-w-md' : ''}>{mock}</BrowserFrame>
                    </div>
                  )}
                </FadeUp>
              ))}
            </div>
          </div>
          <FadeUp className="mt-20">
            <Link to="/signup" data-testid="how-cta-signup" className="btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[15px] font-semibold">
              {t('hero.ctaPrimary')} <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
